import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Tippy from '@tippyjs/react';
import { documentCategories } from 'constants';

import { formatDate, formatDateAndTimeZ, snakeToTitleCase } from 'lib/helpers/utility';
import { useDocumentsCategories } from 'lib/hooks/queries/documents/useDocuments';

import TagsCell from 'components/shared/Tags/components/Cell/TagsCell';

import DocActions from '../components/DocumentActions';

const DocumentCategoryCell = ({ value, patientId }) => {
  const { myPortalPractice } = useParams();
  const { data: categoriesData } = useDocumentsCategories({
    params: {
      patientId,
      staticCategories: documentCategories,
      ...(myPortalPractice && { myPortalPractice })
    },
    dependencies: [myPortalPractice, patientId]
  });

  const categories = useMemo(() => categoriesData?.categories || [], [categoriesData]);

  return (
    <p>
      {categories?.find((cat) => cat?.value?.toString() == value?.toString())
        ? categories?.find((cat) => cat?.value?.toString() == value?.toString())?.label
        : snakeToTitleCase(value)}
    </p>
  );
};
export default (props) => {
  const navigateToPatient = (id) => {
    props?.navigate(`/portal/charts/${id}`);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Document Name',
      minWidth: 250,
      flex: 1,
      headerCheckboxSelection: props?.options?.enableCheckboxSelection,
      checkboxSelection: props?.options?.enableCheckboxSelection
        ? (params) => !params.node.group
        : null
    },
    props?.options?.showPatient
      ? {
          field: 'patient',
          headerName: 'Patient',
          flex: 1,
          cellRenderer: ({ value }) => (
            <span className="text-primary-900">
              <Tippy placement="right" className="tippy-dark" content="Go to Chart">
                <span
                  onClick={() => navigateToPatient(value?.id)}
                  className="!cursor-pointer text-primary-900 hover:text-primary-700 hover:underline">
                  {value?.f_name} {value?.l_name}
                </span>
              </Tippy>
            </span>
          )
        }
      : null,
    {
      field: 'date_of_service',
      headerName: 'Date of Service',
      flex: 1,
      valueFormatter: ({ value }) =>
        value ? formatDateAndTimeZ(value, props?.practice?.timezone) : '-'
    },
    {
      field: 'category',
      headerName: 'Document Category',
      flex: 1,
      cellRenderer: ({ value }) => (
        <DocumentCategoryCell value={value} patientId={props?.patientId} />
      )
    },
    {
      field: 'uploader_name',
      headerName: 'Uploaded by',
      flex: 1
    },
    {
      field: 'created_at',
      headerName: 'Document Date',
      flex: 1,
      valueFormatter: ({ value }) => (value ? formatDate(value, props?.practice?.timezone) : '-')
    },
    {
      field: 'shared_from',
      headerName: 'Shared from location',
      flex: 1,
      hide: true,
      valueFormatter: ({ value }) => (value ? value : '-')
    },
    props?.options?.enableTags
      ? {
          field: 'tagLinks',
          headerName: 'Tags',
          cellClass: 'no-border',
          minWidth: 300,
          cellRenderer: ({ value, data }) => (
            <TagsCell
              value={value}
              kind="document"
              data={data}
              query={{
                keys: [
                  ['practice-documents'],
                  data?.patient_id ? ['chart-documents', data?.patient_id] : null,
                  data?.appointment_id
                    ? ['attachment-documents', data?.appointment_id.toString()]
                    : null
                ].filter(Boolean),
                object: 'documents',
                multiple: true
              }}
              refTable={data?.kind === '' || !data?.kind ? 'patient_documents' : data?.kind}
            />
          ),
          sortable: false
        }
      : null,
    {
      field: 'actions',
      headerName: 'Actions',
      maxWidth: 88,
      minWidth: 36,
      resizable: false,
      sortable: false,
      pinned: 'right',
      cellRenderer: ({ data, index }) => (
        <DocActions
          {...{
            setViewDocument: props?.setViewDocument,
            isEditOpen: props?.isEditOpen,
            setIsEditOpen: props?.setIsEditOpen,
            options: props?.options,
            patientId: props?.patientId,
            appointmentId: props?.appointmentId,

            mimetype: data?.mimetype,
            setPatientSelection: props?.setPatientSelection,
            form: {
              response_id: data?.id,
              appointment_id: data?.appointment_id,
              form_id: data?.form_id,
              json: data?.json,
              form_json: data?.form_json,
              form_version: data?.form_version
            },
            index,
            setSendFaxModal: props?.setSendFaxModal,
            data,
            setShareWithPracticeModal: props?.setShareWithPracticeModal
          }}
        />
      )
    }
  ];
  return columns.filter(Boolean);
};
